<script setup>
defineProps({
  table: {
    type: Array,
    required: true
  }
})
const nowMinRef = ref(null)
const hasSetRef = ref(false)

const setRef = (el, min, content) => {
  if (!content.past && !min.past && !hasSetRef.value) {
    nowMinRef.value = el
    hasSetRef.value = true
  }
}
onMounted(() => {
  if (nowMinRef.value) {
    nowMinRef.value.scrollIntoView({ behavior: 'instant', inline: 'start', block: 'nearest' })
  }
})
</script>

<template>
  <div class="table">
    <ul class="head">
      <li class="hour">
        <p>時</p>
      </li>
      <li class="minute">
        <p>分</p>
      </li>
      <li class="weather">
        <p>天気</p>
      </li>
    </ul>
    <div class="body">
      <div
        v-for="(content, index) in table"
        :key="index"
        class="group"
        :class="content.past && 'past'"
      >
        <div class="hour">
          <p>{{ content.hour }}</p>
        </div>
        <div class="content">
          <ul
            v-for="(min, index2) in content.contents"
            :key="index2"
            :ref="(el) => setRef(el, min, content)"
            class="list"
            :class="min.past && 'past'"
          >
            <li class="minute">
              <p>{{ min.minute }}</p>
            </li>
            <li class="weather">
              <figure
                class="icon"
                :data-line="min.line"
              >
                <img
                  :src="`https://gvs.weathernews.jp/onebox/img/wxicon/${min.weather}@2x.webp`"
                  loading="lazy"
                  alt=""
                >
              </figure>
            </li>
            <li class="comment">
              <p>{{ min.comment }}</p>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.table {
  display: flex;
  position: relative;
  width: 100%;
  height: 236px;
  border: solid 1px #dfe4f0;
  border-radius: 8px;
  background-color: #f9f9f9;
  overflow: hidden;
  .scroll {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgb(0 0 0 / .8);
    z-index: 100;
    transition: opacity .3s ease-in-out;
    img {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 100px;
    }
    &.is-loaded {
      opacity: 0;
    }
  }
  &::after {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    width: 10px;
    height: 100%;
    background: linear-gradient(to right, rgba(0 0 0 / 0), rgba(0 0 0 / .3));
    content: "";
  }
  .head {
    display: flex;
    position: relative;
    flex-direction: column;
    width: 45px;
    height: 100%;
    background-color: #eef1f8;
    box-shadow: 3px 0 6px 0 rgba(0 0 0 / .3);
    z-index: 2;
    .hour,
    .minute {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 22px;
      border-bottom: solid 1px #dfe4f0;
    }
    .weather {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 182px;
    }
    p {
      font-size: 14px;
      line-height: 14px;
      text-align: center;
    }
  }
  .body {
    position: relative;
    display: flex;
    flex: 1;
    overflow-x: scroll;
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 8px;
      background-color: #eef1f8;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 8px;
      background: #438fec;
    }
    .group {
      display: flex;
      flex-direction: column;
      &:not(:first-child) {
        border-left: solid 1px #dfe4f0;
      }
      &:nth-child(2n) {
        .hour {
          background-color: #eef1f8;
        }
      }
      &:has(ul.past:last-child) {
        .hour {
          p {
            color: #a3a3a3;
          }
        }
      }
      .hour {
        display: flex;
        align-items: center;
        height: 22px;
        border-bottom: solid 1px #dfe4f0;
        p {
          position: sticky;
          padding-left: 5px;
          padding-right: 5px;
          left: 0;
          font-size: 13px;
          font-weight: 600;
          line-height: 13px;
          &:is(.past *) {
            color: #a3a3a3;
          }
        }
      }
      .content {
        display: flex;
        flex: 1;
        .list {
          display: flex;
          flex-direction: column;
          width: 65px;
          &:not(:last-child) {
            border-right: solid 1px #dfe4f0;
          }
        }
        .minute {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 22px;
          border-bottom: solid 1px #dfe4f0;
          p {
            font-size: 15px;
            line-height: 15px;
            &:is(.past *) {
              color: #a3a3a3;
            }
          }
        }
        .weather {
          display: flex;
          justify-content: center;
          height: 147px;
          border-bottom: solid 1px #dfe4f0;
          background: linear-gradient(#fcf0e3, #dce7fc);
          position: relative;
          .icon {
            position: absolute;
            width: 40px;
            aspect-ratio: 1 / 1;
            &[data-line='1'] {
              top: 67.3%;
            }
            &[data-line='2'] {
              top: 58.4%;
            }
            &[data-line='3'] {
              top: 49.5%;
            }
            &[data-line='4'] {
              top: 40.6%;
            }
            &[data-line='5'] {
              top: 31.7%;
            }
            &[data-line='6'] {
              top: 22.8%;
            }
            &[data-line='7'] {
              top: 13.9%;
            }
            &[data-line='8'] {
              top: 5%;
            }
            &:is(.past *) {
              filter: grayscale(1);
              opacity: 0.45;
            }
            img {
              width: 100%;
            }
          }
        }
        .comment {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 35px;
          border-bottom: solid 1px #dfe4f0;
          p {
            font-size: 12px;
            font-weight: 600;
            text-align: center;
            &:is(.past *) {
              color: #a3a3a3;
            }
          }
        }
      }
    }
  }
}
</style>
